// ============================================================================
// LAYOUT CONSTANTS
// ============================================================================

/**
 * Container margin for full-width layouts
 * Used to create consistent spacing from viewport edges
 */
export const FULL_WIDTH_CONTAINER_MARGIN = 48;

/**
 * MAP GRID LAYOUTS
 * Controls the proportion between map and accommodations sections
 * Format: 'mapProportion accommodationsProportion'
 */

// Standard container layouts (not full-width)
export const MAP_GRID_STANDARD = '1.5fr 2fr'; // Map gets 1.5fr, accommodations get 2fr
export const MAP_GRID_STANDARD_WITH_FEW_HOTELS = '3fr 2fr'; // When <4 accommodations, give map more space

// Full-width container layouts with responsive breakpoints
export const MAP_GRID_FULL_WIDTH_STANDARD = {
  sm: '1fr 1fr', // Equal split on small screens
  md: '3fr 7fr', // Accommodations get more space on medium
  md3: '2fr 3fr', // Balanced split on medium-large
};

export const MAP_GRID_FULL_WIDTH_WITH_FEW_HOTELS = {
  sm: '1fr 1fr', // Equal split on small
  md: '3fr 7fr', // Accommodations get more space on medium
  md2: '2fr 3fr', // Balanced on medium-small
  md4: '1fr 1fr', // Equal on medium-large
  lg: '4fr 3fr', // Map gets more space on large
};

export const MAP_GRID_FULL_WIDTH_WITH_FEW_HOTELS_STANDARD_TILES = {
  sm: '1fr 1fr', // Equal split on small
  md: '3fr 2fr', // Map gets more space on medium
  md3: '1fr 1fr', // Equal on medium-large
  lg: '3.5fr 3fr', // Slightly more space for map on large
};

/**
 * ACCOMMODATIONS GRID LAYOUTS
 * Controls how many accommodation tiles per row
 * Format: 'repeat(columns, 1fr)'
 */

// Standard container layouts
export const ACCOMMODATIONS_GRID_STANDARD = 'repeat(2, 1fr)'; // 2 columns
export const ACCOMMODATIONS_GRID_STANDARD_WITH_FEW_HOTELS = 'repeat(1, 1fr)'; // 1 column when <4 accommodations
export const ACCOMMODATIONS_GRID_STANDARD_HORIZONTAL = 'repeat(1, 1fr)'; // 1 column for horizontal layout

// Full-width container layouts with responsive breakpoints
export const ACCOMMODATIONS_GRID_FULL_WIDTH_STANDARD = {
  sm: 'repeat(1, 1fr)', // 1 column on small
  md: 'repeat(2, 1fr)', // 2 columns on medium
  lg: 'repeat(3, 1fr)', // 3 columns on large
};

export const ACCOMMODATIONS_GRID_FULL_WIDTH_WITH_FEW_HOTELS_STANDARD_TILES = {
  sm: 'repeat(1, 1fr)', // 1 column on small
  md: 'repeat(1, 1fr)', // 1 column on medium
  md3: 'repeat(1, 1fr)', // 1 column on medium-large
  lg: 'repeat(3, 1fr)', // 3 columns on large
};

export const ACCOMMODATIONS_GRID_FULL_WIDTH_HORIZONTAL = {
  _: 'repeat(1, 1fr)', // Always 1 column for horizontal layout
};

/**
 * SPACING CONSTANTS
 * Reusable spacing values for consistent layout
 */
export const SPACING = {
  ELEMENT_GAP_SMALL: 8, // Small gap between elements
  ELEMENT_GAP_MEDIUM: 12, // Medium gap between elements
  ELEMENT_GAP_LARGE: 24, // Large gap between elements
  ELEMENT_GAP_EXTRA_LARGE: 32, // Extra large gap for full-width layouts
  CONTAINER_PADDING_DEFAULT: 8, // Default container padding
  MAP_MINIMUM_HEIGHT: 300, // Minimum height for map container
  MAP_STICKY_TOP_OFFSET: 48, // Top offset for sticky map positioning
  MAP_VIEWPORT_HEIGHT_OFFSET: 96, // Offset from viewport height for map calculation
} as const;

/**
 * LAYOUT DIMENSIONS
 * Fixed dimensions used throughout the component
 */
export const LAYOUT_DIMENSIONS = {
  SINGLE_HOTEL_CONTAINER_WIDTH: 758, // Fixed width for single hotel view containers
} as const;
